import * as Sentry from '@sentry/nextjs';
import type { 
  Question, 
  AuthResponse, 
  LoginRequest, 
  UserResponses, 
  UserResults,
  ApiError,
  ApiResponse,
  ApiSuccessResponse,
  SendResultsEmailRequest
} from '@/types';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api';

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_URL;
  }

  // Helper method for making HTTP requests
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        let errorData: ApiError;
        try {
          errorData = await response.json();
        } catch {
          errorData = {
            message: `HTTP ${response.status}: ${response.statusText}`,
            statusCode: response.status
          };
        }
        throw new Error(errorData.message || 'Error en la petición');
      }

      return response.json();
    } catch (error) {
      // Capture error in Sentry with context
      Sentry.captureException(error, {
        tags: {
          section: 'api_request',
          endpoint: endpoint
        },
        extra: {
          url,
          config: {
            method: config.method || 'GET',
            headers: Object.keys(config.headers || {}),
          }
        }
      });

      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`No se pudo conectar al servidor.`);
      }
      
      throw error;
    }
  }

  // Helper to add Authorization header
  private getAuthHeaders(token?: string): Record<string, string> {
    const authToken = token || this.getStoredToken();
    if (authToken) {
      return { Authorization: `Bearer ${authToken}` };
    } else {
      return {};
    }
  }

  // Token management
  private getStoredToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
  }

  private setStoredToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('auth_token', token);
  }

  private removeStoredToken(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('auth_token');
  }

  // Authentication API
  async emailLogin(loginData: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<ApiResponse<AuthResponse>>('/auth/email-login', {
      method: 'POST',
      body: JSON.stringify(loginData),
    });
    
    // Extract the auth data from the API response
    const authData = response.data;
    
    // Store token for subsequent requests
    if (authData && authData.access_token) {
      this.setStoredToken(authData.access_token);
    } else {
      const errorMsg = 'No se recibió un token válido del servidor';
      Sentry.captureException(new Error(errorMsg), {
        tags: {
          section: 'authentication',
          operation: 'email_login'
        },
        extra: {
          hasResponse: !!response,
          hasData: !!response.data,
          responseKeys: response.data ? Object.keys(response.data) : []
        }
      });
      throw new Error(errorMsg);
    }
    
    return authData;
  }

  // Public API - Questions (no authentication required)
  async getPublicQuestions(areaId?: number): Promise<Question[]> {
    const endpoint = areaId 
      ? `/preguntas/public?areaId=${areaId}`
      : '/preguntas/public';
    
    const response = await this.request<ApiResponse<Question[]>>(endpoint);
    
    // Extract the data array from the API response
    return response.data || [];
  }

  // Get all areas from questions data
  async getAreasFromQuestions(): Promise<Array<{id: number, name: string, description: string}>> {
    const questions = await this.getPublicQuestions();
    
    if (!Array.isArray(questions)) {
      const errorMsg = 'La respuesta de preguntas no es válida';
      Sentry.captureException(new Error(errorMsg), {
        tags: {
          section: 'questions',
          operation: 'get_areas_from_questions'
        },
        extra: {
          questionsType: typeof questions,
          questionsValue: questions
        }
      });
      throw new Error(errorMsg);
    }
    
    if (questions.length === 0) {
      return [];
    }
    
    const areasMap = new Map();
    
    questions.forEach(question => {
      if (!areasMap.has(question.areaId)) {
        areasMap.set(question.areaId, question.area);
      }
    });
    
    return Array.from(areasMap.values()).sort((a, b) => a.id - b.id);
  }

  // Submit responses (requires authentication)
  async submitResponses(responses: UserResponses): Promise<ApiSuccessResponse> {
    const response = await this.request<ApiSuccessResponse>('/respuestas', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      body: JSON.stringify(responses),
    });
    
    return response;
  }

  // Get results (requires authentication)
  async getResults(email: string): Promise<UserResults> {
    const response = await this.request<ApiResponse<UserResults> | UserResults>(`/resultados/${encodeURIComponent(email)}`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
    });
    
    // Check if response is wrapped in data object
    if (response && typeof response === 'object' && 'data' in response) {
      return (response as ApiResponse<UserResults>).data;
    }
    
    return response as UserResults;
  }

  // Send results by email (requires authentication with temporary token)
  async sendResultsByEmail(data: SendResultsEmailRequest): Promise<ApiSuccessResponse> {
    const response = await this.request<ApiSuccessResponse>('/enviar-resultados', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      body: JSON.stringify(data),
    });
    
    return response;
  }

  // Logout
  logout(): void {
    this.removeStoredToken();
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }
}

// Create and export singleton instance
export const apiService = new ApiService();

// Export individual methods bound to the instance to preserve 'this' context
export const emailLogin = apiService.emailLogin.bind(apiService);
export const getPublicQuestions = apiService.getPublicQuestions.bind(apiService);
export const getAreasFromQuestions = apiService.getAreasFromQuestions.bind(apiService);
export const submitResponses = apiService.submitResponses.bind(apiService);
export const getResults = apiService.getResults.bind(apiService);
export const sendResultsByEmail = apiService.sendResultsByEmail.bind(apiService);
export const logout = apiService.logout.bind(apiService);
export const isAuthenticated = apiService.isAuthenticated.bind(apiService);